// ============================================================
// SIE-fil-parser (Standard Import Export) — svensk bokförings­standard.
// Stödjer SIE4-format som genereras av Fortnox, Visma, Björn Lundén m.fl.
// Läser verifikat + transaktionsrader, aggregerar per månad.
// ============================================================

import type { MonthlyRow } from '../data/store'

export interface SieParseResult {
  companyName?: string
  fiscalYearStart?: string // YYYY-MM-DD
  fiscalYearEnd?: string
  months: MonthlyRow[] // length 12
  warnings: string[]
  rowCount: number
}

/** BAS-kontoplan klassificering */
function classifyAccount(konto: number): 'income' | 'expense' | 'vat-in' | 'vat-out' | 'other' {
  // 3000-3999 = Intäkter (försäljning)
  if (konto >= 3000 && konto <= 3999) return 'income'
  // 4000-7999 = Kostnader (varukostnad, drift, personal, övriga)
  if (konto >= 4000 && konto <= 7999) return 'expense'
  // 2611, 2614, 2621, 2631, 2641 = Utgående moms (VI SÄLJER) → pratar om konto 26
  // 2640-2649 = Ingående moms (VI KÖPER)
  // Förenklad mapp:
  //   26x0-26x9: utgående (på försäljning)
  //   264x: ingående (på inköp)
  if (konto >= 2640 && konto <= 2649) return 'vat-in'
  if (konto >= 2610 && konto <= 2639) return 'vat-out'
  return 'other'
}

function emptyMonths(): MonthlyRow[] {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i,
    vatIn: 0,
    vatOut: 0,
    income: 0,
    expense: 0,
  }))
}

/**
 * Dela en SIE-rad med respekt för "quoted strings".
 * Exempel: #TRANS 3010 {} 1000.00 "2026-03-15" "Försäljning till kund"
 */
function tokenize(line: string): string[] {
  const tokens: string[] = []
  let cur = ''
  let inQuote = false
  let inBrace = 0
  for (const ch of line) {
    if (inQuote) {
      if (ch === '"') {
        inQuote = false
        tokens.push(cur)
        cur = ''
      } else {
        cur += ch
      }
    } else if (inBrace > 0) {
      if (ch === '{') inBrace++
      else if (ch === '}') {
        inBrace--
        if (inBrace === 0) {
          tokens.push('{' + cur + '}')
          cur = ''
        } else {
          cur += ch
        }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"') {
        if (cur) {
          tokens.push(cur)
          cur = ''
        }
        inQuote = true
      } else if (ch === '{') {
        if (cur) {
          tokens.push(cur)
          cur = ''
        }
        inBrace = 1
      } else if (ch === ' ' || ch === '\t') {
        if (cur) {
          tokens.push(cur)
          cur = ''
        }
      } else {
        cur += ch
      }
    }
  }
  if (cur) tokens.push(cur)
  return tokens
}

/** Parse date as YYYYMMDD or YYYY-MM-DD */
function parseDate(raw: string): Date | null {
  if (!raw) return null
  const clean = raw.replace(/[^\d]/g, '')
  if (clean.length !== 8) return null
  const y = parseInt(clean.slice(0, 4), 10)
  const m = parseInt(clean.slice(4, 6), 10) - 1
  const d = parseInt(clean.slice(6, 8), 10)
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null
  return new Date(y, m, d)
}

/**
 * Parsa SIE4-innehåll. Alla teckensnitt stöds (UTF-8 + CP437 som är SIE-standard men
 * de flesta moderna exporter är UTF-8).
 *
 * Fokus: aggregera månadsbelopp för momskonton och intäkts/kostnadskonton
 * från #VER-block med #TRANS-rader. Datum tas från verifikatet (VER-raden).
 */
export function parseSie(text: string, targetYear?: number): SieParseResult {
  const lines = text.split(/\r?\n/)
  const result: SieParseResult = {
    months: emptyMonths(),
    warnings: [],
    rowCount: 0,
  }

  let currentVerDate: Date | null = null
  let inVer = false

  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line || line.startsWith('//')) continue

    // #BLOCK or {} markers — skip braces-only lines
    if (line === '{' || line === '}') {
      if (line === '}') inVer = false
      continue
    }

    const tokens = tokenize(line)
    if (tokens.length === 0) continue
    const directive = tokens[0]

    switch (directive) {
      case '#FNAMN':
        result.companyName = tokens[1] ?? result.companyName
        break

      case '#RAR':
        // #RAR 0 20260101 20261231  = räkenskapsår 0 (innevarande)
        if (tokens[1] === '0') {
          const s = parseDate(tokens[2])
          const e = parseDate(tokens[3])
          if (s) result.fiscalYearStart = s.toISOString().slice(0, 10)
          if (e) result.fiscalYearEnd = e.toISOString().slice(0, 10)
        }
        break

      case '#VER': {
        // #VER "serie" "vernr" "verdatum" "text"
        // verdatum brukar ligga som index 3
        const dateStr =
          tokens[3] && tokens[3].length >= 6 ? tokens[3] : tokens[2] ?? ''
        currentVerDate = parseDate(dateStr)
        inVer = true
        break
      }

      case '#TRANS': {
        // #TRANS konto {} belopp [transdatum] [transtext] [kvantitet] [sign]
        if (!inVer) break
        const konto = parseInt(tokens[1] ?? '0', 10)
        // tokens[2] är ofta {objekt} — hoppa över
        // Belopp ligger vanligtvis på index 3
        const belopp = parseFloat((tokens[3] ?? '0').replace(',', '.'))
        if (isNaN(konto) || isNaN(belopp)) break

        // Om transdatum finns så använder vi det; annars verdatum
        let transDate: Date | null = null
        for (let i = 4; i < tokens.length; i++) {
          const d = parseDate(tokens[i])
          if (d) {
            transDate = d
            break
          }
        }
        const date = transDate ?? currentVerDate
        if (!date) break

        if (targetYear != null && date.getFullYear() !== targetYear) break

        const m = date.getMonth()
        const kind = classifyAccount(konto)
        result.rowCount++

        // SIE-konvention: intäkter är KREDIT = negativt belopp, kostnader är DEBET = positivt.
        // Vi vill ha positiva värden i vår store, så vi tar Math.abs()
        const abs = Math.abs(belopp)

        switch (kind) {
          case 'income':
            // Intäktskonto har normalt negativt saldo (kredit). Bara räkna poster där kredit ökar (belopp < 0).
            // Men för att vara robust mot olika exporter: om belopp är negativt eller positivt, addera abs.
            if (belopp < 0) result.months[m].income += abs
            else result.months[m].income -= abs // korrigering/kreditering
            break
          case 'expense':
            if (belopp > 0) result.months[m].expense += abs
            else result.months[m].expense -= abs
            break
          case 'vat-in':
            // Ingående moms — vanligtvis debet (positiv)
            if (belopp > 0) result.months[m].vatIn += abs
            else result.months[m].vatIn -= abs
            break
          case 'vat-out':
            // Utgående moms — vanligtvis kredit (negativ)
            if (belopp < 0) result.months[m].vatOut += abs
            else result.months[m].vatOut -= abs
            break
          case 'other':
            // Inte relevant för våra vyer
            break
        }
        break
      }

      default:
        // Ignorera andra direktiv (#ORGNR, #FLAGGA, #KPTYP, etc.)
        break
    }
  }

  if (result.rowCount === 0) {
    result.warnings.push(
      'Inga verifikat med transaktioner hittades. Är det en SIE1-fil? (bara saldon, inte verifikat)'
    )
  }

  return result
}
